import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import RectifyBirthTimeModal from '@/components/RectifyBirthTimeModal';
import { toast } from '@/lib/toast';

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [date, setDate] = useState(user?.birth_details?.date || '');
    const [time, setTime] = useState(user?.birth_details?.time || '');
    const [placeText, setPlaceText] = useState(user?.birth_details?.place || '');
    const [resolved, setResolved] = useState(user?.birth_details || null);
    const [confidence, setConfidence] = useState(
        user?.birth_time_confidence || 'not_set'
    );
    const [saving, setSaving] = useState(false);
    const [showRectifyModal, setShowRectifyModal] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if (!date || !time || !placeText.trim()) {
            toast.error('Date, time and place are all required');
            return;
        }
        setSaving(true);
        try {
            // Reuse coords if input matches; otherwise geocode the typed text
            let coords = resolved;
            if (!coords || coords.place !== placeText) {
                const { data } = await api.post('/astrology/geocode', { place: placeText });
                coords = data;
            }
            const { data: saved } = await api.put('/auth/me', {
                birth_details: {
                    date, time,
                    place: coords.place,
                    lat: coords.lat,
                    lon: coords.lon,
                    tz: coords.tz,
                },
                birth_time_confidence: confidence,
            });
            await refresh();
            if (saved?.duplicate_warning) {
                toast.error(saved.duplicate_warning.message, { duration: 9000 });
            } else {
                toast.success('Saved — your chart is ready');
            }
            navigate('/app');
        } catch (err) {
            toast.error(readErr(err, 'Could not resolve place — pick from the dropdown'));
        } finally { setSaving(false); }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center px-6 py-12">
            <Card className="relative z-10 w-full max-w-xl bg-elevated border-marigold/15 rounded-sm p-8 md:p-12" data-testid="onboarding-card">
                <div className="overline mb-4">One-time setup</div>
                <h1 className="font-serif text-3xl md:text-4xl text-parchment mb-2">Your birth coordinates</h1>
                <p className="text-sm text-slate-400 mb-10">
                    Stored once. Used for every natal reading. Be as precise with the time as
                    you can — minutes matter for the ascendant.
                </p>

                <form onSubmit={save} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">Date of birth</Label>
                            <Input type="date" required value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11 focus-visible:ring-1 focus-visible:ring-marigold"
                                data-testid="onboard-date" />
                        </div>
                        <div>
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">Time of birth</Label>
                            <Input type="time" required value={time}
                                onChange={(e) => setTime(e.target.value)}
                                className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11 focus-visible:ring-1 focus-visible:ring-marigold"
                                data-testid="onboard-time" />
                        </div>
                    </div>

                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Place of birth</Label>
                        <div className="mt-2">
                            <PlaceAutocomplete
                                value={placeText}
                                onTextChange={setPlaceText}
                                onSelect={(loc) => { setResolved(loc); setPlaceText(loc.place); }}
                                placeholder="Start typing your birth city — Mumbai, Karachi, London…"
                                testId="onboard-place"
                            />
                        </div>
                        {resolved && resolved.place === placeText && (
                            <div className="mt-3 text-xs text-marigold/90" data-testid="geocode-result">
                                {resolved.lat}, {resolved.lon} · {resolved.tz}
                            </div>
                        )}
                    </div>

                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">
                            How confident are you about this birth time?
                        </Label>
                        <div className="mt-3 grid gap-2" data-testid="onboard-confidence">
                            {[
                                { v: 'exact', title: 'Exact', desc: 'From hospital records or an official document' },
                                { v: 'approximate', title: 'Approximate', desc: 'Family memory — within 30 to 60 minutes' },
                                { v: 'unknown', title: 'Unknown', desc: 'Birth time is not known — engine will use horary' },
                            ].map((o) => (
                                <label
                                    key={o.v}
                                    data-testid={`onboard-confidence-${o.v}`}
                                    className={`cursor-pointer p-4 rounded-sm border transition-all ${
                                        confidence === o.v
                                            ? 'bg-marigold/5 border-marigold'
                                            : 'bg-surface border-marigold/10 hover:border-marigold/30'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="birth-time-confidence"
                                        value={o.v}
                                        checked={confidence === o.v}
                                        onChange={() => setConfidence(o.v)}
                                        className="sr-only"
                                    />
                                    <div className="font-serif text-parchment text-base">{o.title}</div>
                                    <div className="text-xs text-slate-400 mt-1">{o.desc}</div>
                                </label>
                            ))}
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                            Optional — if unsure, skip. Approximate or unknown times switch specific
                            event questions to horary automatically.
                        </p>
                        {(confidence === 'approximate' || confidence === 'unknown') && (
                            <div
                                className="mt-4 p-4 bg-marigold/5 border border-marigold/20 rounded-sm"
                                data-testid="onboard-rectify-prompt"
                            >
                                <p className="text-sm text-slate-300">
                                    Birth-time accuracy shapes the precision of your readings. We can analyse
                                    your birth time using the KP Ruling Planets method and suggest a more
                                    precise time.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!date || !time || !placeText.trim()) {
                                            toast.info('Please save your birth details first, then run the analysis.');
                                            return;
                                        }
                                        setShowRectifyModal(true);
                                    }}
                                    className="mt-3 text-sm text-marigold hover:text-marigold-50"
                                    data-testid="onboard-rectify-cta"
                                >
                                    Analyse my birth time — ₹2,000 →
                                </button>
                            </div>
                        )}
                    </div>

                    <Button type="submit" disabled={saving || !date || !time || !placeText.trim()}
                        data-testid="onboard-submit"
                        className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 font-medium">
                        {saving ? 'Saving…' : 'Save & continue'}
                    </Button>
                </form>
            </Card>
            <RectifyBirthTimeModal
                open={showRectifyModal}
                onOpenChange={setShowRectifyModal}
                onSuccess={async () => { await refresh(); }}
            />
        </div>
    );
}
